import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useGetAgencyMetrics } from '../../../../api/agencies/reports/useGetAgencyMetrics'
import LineChart from '../../../../components/Charts/LineChart'
import dayjs from 'dayjs'
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from '../../../../lib/utils/formatNumber'
import MultiLineChart from '../../../../components/Charts/MultiLineChart'
import BarChart from '../../../../components/Charts/BarChart'
import { AsyncSelectInput, Button, Table } from '@demandlocal/components'
import { useDownloadPDFReport } from '../../../../api/dealership/reports/useDownloadPDFReport'
import { ReactComponent as DownloadIcon } from '../../../../images/icons/action_download.svg'
import toast from 'react-hot-toast'
import { GA4 } from '../../../../lib/utils/ga4'
import { createColumnHelper } from '@tanstack/react-table'
import { Link, useSearchParams } from 'react-router-dom'
import { useGetCampaignsByAgencyId } from '../../../../api/agencies/useGetCampaignsByAgencyId'
import FiltersContext from '../../../../components/Filters/FiltersContext'
import { createHtmlPortalNode, OutPortal } from 'react-reverse-portal'
import Select from 'react-select'
import { useGetAgencyProfileActiveClients } from '../../../../api/agencies/useGetAgencyProfileActiveClients'
import { useTranslation } from 'react-i18next'
import { ReactComponent as ChevronLeft } from '../../../../images/icons/chevron_left.svg'
import downloadCSV from '../../../../api/downloadCSV'
import { useGetAgencyByAgencyId } from '../../../../api/agencies/useGetAgencyByAgencyId'
import { AgencyCampaign } from './AgencyCampaigns'

const campaignColumnHelper = createColumnHelper<AgencyCampaign>()

const YearlyOverview = (): JSX.Element => {
  const { agency_id } = useParams()
  const filtersPortalNode = useMemo(() => createHtmlPortalNode(), [])
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: agency } = useGetAgencyByAgencyId(agency_id)
  const [downloadingTable, setDownloadingTable] = useState<boolean>(false)

  const { t } = useTranslation('routes')
  const navigate = useNavigate()
  const [filters, setFilters] = useState<Record<string, unknown>>({
    dealership_id: searchParams
      .getAll('dealership')
      ?.map((d) => Number(d))
      ?.map((d) => ({ label: '', value: d })),
  })

  const [dateFilter, setDateFilter] = React.useState<{
    label: string
    value: string
  }>({
    label: searchParams.get('year') ?? dayjs().year().toString(),
    value: searchParams.get('year') ?? dayjs().year().toString(),
  })

  const { data: dealerships, isLoading: isLoadingDealerships } =
    useGetAgencyProfileActiveClients({
      pageIndex: 1,
      pageSize: 100,
      filters: { agency_id },
    })

  useEffect(() => {
    const urlDealers = searchParams.getAll('dealership')?.map((d) => Number(d))
    if (urlDealers?.length > 0 && !isLoadingDealerships) {
      setFilters((prev) => {
        return {
          ...prev,
          dealership_id: dealerships?.data
            ?.filter((d) => urlDealers?.includes(d.id))
            ?.map((d) => ({ label: d.dealership, value: d.id })),
        }
      })
    }
  }, [dealerships, isLoadingDealerships, searchParams])

  useEffect(() => {
    setSearchParams((sp) => {
      sp.set('year', dateFilter?.value)
      if (sp.has('dealership')) sp.delete('dealership')
      if (filters?.dealership_id && Array.isArray(filters?.dealership_id)) {
        filters?.dealership_id?.forEach((v) =>
          sp.append('dealership', v.value.toString()),
        )
      }
      return sp
    })
  }, [dateFilter?.value, filters?.dealership_id, searchParams, setSearchParams])

  const {
    data: metrics = [],
    isLoading: isLoadingMetrics,
    isPreviousData: isPreviousMetrics,
  } = useGetAgencyMetrics({
    agency_id: Number(agency_id),
    filters: {
      start_date: dayjs()
        .set('year', Number(dateFilter.value))
        .startOf('year')
        .format('YYYY-MM-DD'),
      end_date: dayjs()
        .set('year', Number(dateFilter.value))
        .endOf('year')
        .format('YYYY-MM-DD'),
      dealership_id: filters?.dealership_id,
    },
  })

  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport({
      width: 4000,
      height: 5000,
    })

  useEffect(() => {
    if (!isLoadingMetrics) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoadingMetrics])

  const hasAudioImpressions = useMemo(
    () =>
      Number(
        metrics
          ?.map((metric: { audio_impressions: number }) => metric.audio_impressions)
          .reduce((acc: number, cur: number) => acc + cur, 0),
      ) > 0,
    [metrics],
  )

  const hasVideoImpressions = useMemo(
    () =>
      Number(
        metrics
          ?.map((metric: { video_starts: number }) => metric.video_starts)
          .reduce((acc: number, cur: number) => acc + cur, 0),
      ) > 0,
    [metrics],
  )

  const barChartData = useMemo(
    () =>
      metrics.reduce(
        (acc, cur) => {
          const {
            video_starts,
            impressions,
            audio_impressions,
            video_first_quartile_completions,
            video_second_quartile_completions,
            video_third_quartile_completions,
            video_completions,
            audio_first_quartile_completions,
            audio_second_quartile_completions,
            audio_third_quartile_completions,
            audio_completions,
          } = cur
          acc.impressions += impressions
          acc.video_starts += video_starts
          // Video metrics
          acc.video_1q += video_first_quartile_completions
          acc.video_2q += video_second_quartile_completions
          acc.video_3q += video_third_quartile_completions
          acc.video_4q += video_completions
          // Audio metrics
          acc.audio_impressions += audio_impressions
          acc.audio_1q += audio_first_quartile_completions
          acc.audio_2q += audio_second_quartile_completions
          acc.audio_3q += audio_third_quartile_completions
          acc.audio_4q += audio_completions

          return acc
        },
        {
          impressions: 0,
          video_starts: 0,
          video_1q: 0,
          video_2q: 0,
          video_3q: 0,
          video_4q: 0,
          // Audio metrics
          audio_impressions: 0,
          audio_1q: 0,
          audio_2q: 0,
          audio_3q: 0,
          audio_4q: 0,
        },
      ),
    [metrics],
  )

  const campaignsColumns = useMemo(
    () => [
      campaignColumnHelper.accessor('dealership_name', {
        header: 'Client',
        cell: ({ getValue, row }) => (
          <Link
            to={t('dealership.profile', {
              dealership: {
                organization_id: agency_id,
                id: row.original.dealership_id,
              },
            })}
            className="text-primary-persian-blue font-bold">
            {getValue()}
          </Link>
        ),
        enableSorting: false,
      }),
      campaignColumnHelper.accessor('client_campaign_display_name', {
        header: 'Campaign',
        cell: ({ getValue, row }) => (
          <Link
            to={t('dealership.campaigns.report', {
              dealership: {
                organization_id: agency_id,
                id: row.original.dealership_id,
              },
              campaign: {
                product_code: row.original.product_code,
                client_campaign_id: row.original.client_campaign_id,
                start_date: row.original.start_date,
                end_date: row.original.end_date,
              },
            })}
            className="text-primary-persian-blue font-bold">
            {getValue()}
          </Link>
        ),
      }),
      campaignColumnHelper.accessor('date', {
        header: 'Month',
        cell: ({ getValue }) => dayjs(getValue()).format('MMM, YYYY'),
        sortingFn: (a, b) =>
          dayjs(a.getValue('date')).isBefore(b.getValue('date')) ? -1 : 1,
      }),
      campaignColumnHelper.accessor('gross_revenue', {
        header: 'Budget',
        cell: ({ getValue }) => formatCurrency(getValue()),
      }),
      campaignColumnHelper.accessor('impressions', {
        header: 'Imps',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
      campaignColumnHelper.accessor('booked_impressions', {
        header: 'Booked Impressions',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
      campaignColumnHelper.accessor('clicks', {
        header: 'Clicks',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
      campaignColumnHelper.accessor('views', {
        header: 'Views/Listens',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
      campaignColumnHelper.accessor('ctr', {
        header: 'CTR',
        cell: ({ getValue }) => (getValue() ? formatPercentage(getValue() / 100) : 'N/A'),
      }),
      campaignColumnHelper.accessor('vtr', {
        header: 'VTR',
        cell: ({ getValue }) => (getValue() ? `${getValue()}%` : 'N/A'),
      }),
      campaignColumnHelper.accessor('delivery', {
        header: 'Delivery',
        cell: ({ getValue }) => (getValue() ? `${getValue()}%` : 'N/A'),
      }),
      campaignColumnHelper.accessor('rsv', {
        header: 'Walk Ins',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
      // Hide for Tire Props since no DMS
      ...(Number(agency_id) !== 114
        ? [
            campaignColumnHelper.accessor('sales', {
              header: 'Attributed Sales',
              enableSorting: false,
              cell: ({ getValue }) => formatNumber(getValue() ?? 0),
            }),
          ]
        : []),
    ],
    [agency_id, t],
  )

  return (
    <div className={'flex flex-col'} id={'content'}>
      <div className="flex flex-1 flex-col gap-4">
        <div className="flex flex-row justify-between items-center w-full print:hidden">
          <div>
            <Button
              variant="tertiary"
              onClick={(e) => {
                e.preventDefault()
                navigate(t('agency.profile.campaigns.list', { agency_id }))
              }}>
              <div className="flex flex-row gap-2 items-center">
                <ChevronLeft fill="#000" />
                <span>Return to list</span>
              </div>
            </Button>
          </div>
          <div className="flex flex-row gap-2 items-center">
            <div className={'flex flex-row gap-2 items-center'}>
              <span>Showing</span>
              <Select
                name={'date_select'}
                options={[
                  // Options: this year or last year
                  {
                    label: dayjs().year().toString(),
                    value: dayjs().year().toString(),
                  },
                  {
                    label: dayjs().subtract(1, 'year').year().toString(),
                    value: dayjs().subtract(1, 'year').year().toString(),
                  },
                ]}
                value={dateFilter}
                onChange={(e) => {
                  e && setDateFilter({ label: e?.label, value: e?.value })
                }}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: '2px',
                    minWidth: 185,
                    minHeight: 36,
                    fontFamily: 'Spartan',
                    fontWeight: 'bold',
                    fontSize: 12,
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    minHeight: 36,
                  }),
                  indicatorsContainer: (base) => ({
                    ...base,
                    height: 36,
                  }),
                  menu: (base) => ({
                    ...base,
                    width: 'max-content',
                    minWidth: '100%',
                    fontFamily: 'Spartan',
                    fontWeight: 'bold',
                    fontSize: 12,
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    color: '#09121F', // Custom colour
                    marginTop: 0,
                  }),

                  clearIndicator: (base) => ({
                    ...base,
                    marginTop: 4,
                    color: '#09121F',
                  }),
                }}
              />
            </div>
            <OutPortal node={filtersPortalNode} />
            <div>
              <Button
                disabled={isLoadingMetrics || isDownloadingReport}
                leftSlot={<DownloadIcon className={'fill-white'} />}
                onClick={async (e) => {
                  e.preventDefault()
                  const toastId = toast.loading('Generating report...')
                  GA4.sendEvent({
                    event_name: 'export_pdf',
                    event_params: {
                      campaign_type: 'YTD',
                    },
                  })
                  await downloadReport(undefined, {
                    async onSuccess(data) {
                      toast.success('Report generated successfully', { id: toastId })
                      const blob = await data.blob()
                      const url = window.URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `YTD.pdf`
                      document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                      a.click()
                      a.remove()
                    },
                  })
                }}>
                Export as PDF
              </Button>
            </div>
          </div>
        </div>
        <div className="print:hidden">
          <FiltersContext
            portalNode={filtersPortalNode}
            setFilters={setFilters}
            defaultValues={filters}>
            <AsyncSelectInput
              label="Clients"
              name={'dealership_id'}
              defaultFetchFnFilters={{
                agency_id,
              }}
              fetchFn={useGetAgencyProfileActiveClients}
              labelKeyName={'dealership'}
              valueKeyName={'id'}
              searchKeyName={'dealership'}
              isClearable
              isMulti
              isSearchable
            />
          </FiltersContext>
        </div>
      </div>
      <div className="flex flex-col gap-8">
        <div className="grid grid-cols-2 gap-5">
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>Total Impressions</span>
              <span>
                {!isLoadingMetrics &&
                  new Intl.NumberFormat('en-US').format(
                    metrics
                      ?.map((metric: { impressions: number }) => metric.impressions)
                      .reduce((acc: number, cur: number) => acc + cur, 0),
                  )}
              </span>
            </div>
            <div className={'h-[270px] p-2'}>
              <LineChart
                data={
                  metrics
                    ?.sort((a, b) => (a.date > b.date ? 1 : -1))
                    ?.map((data) => {
                      return {
                        date: dayjs(data.date).format("MMM 'YY"),
                        impressions: data.impressions,
                      }
                    }) ?? []
                }
                gradient={'green-to-cyan'}
                yKey={'impressions'}
                xKey="date"
                isLoading={isLoadingMetrics}
                isPreviousData={isPreviousMetrics}
              />
            </div>
          </div>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>Total Clicks</span>
              <span>
                {!isLoadingMetrics &&
                  new Intl.NumberFormat('en-US').format(
                    metrics
                      ?.map((metric: { clicks: number }) => metric.clicks)
                      .reduce((acc: number, cur: number) => acc + cur, 0),
                  )}
              </span>
            </div>
            <div className={'h-[270px] p-2'}>
              <LineChart
                data={
                  metrics
                    ?.sort((a, b) => (a.date > b.date ? 1 : -1))
                    ?.map((data) => {
                      return {
                        date: dayjs(data.date).format("MMM 'YY"),
                        clicks: data.clicks,
                      }
                    }) ?? []
                }
                gradient={'red-to-orange'}
                yKey={'clicks'}
                xKey="date"
                isLoading={isLoadingMetrics}
                isPreviousData={isPreviousMetrics}
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-5">
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>Total CTR</span>
              <span>
                {!isLoadingMetrics &&
                  formatPercentage(
                    metrics
                      ?.map((metric: { clicks: number }) => metric.clicks)
                      .reduce((acc: number, cur: number) => acc + cur, 0) /
                      metrics
                        ?.map((metric: { impressions: number }) => metric.impressions)
                        .reduce((acc: number, cur: number) => acc + cur, 0),
                  )}
              </span>
            </div>
            <div className={'h-[270px] p-2'}>
              <LineChart
                data={
                  metrics
                    ?.sort((a, b) => (a.date > b.date ? 1 : -1))
                    ?.map((data) => {
                      return {
                        date: dayjs(data.date).format("MMM 'YY"),
                        ctr: (100 * data.clicks) / data.impressions,
                      }
                    }) ?? []
                }
                gradient={'blue-to-purple'}
                yKey={'ctr'}
                xKey="date"
                isLoading={isLoadingMetrics}
                isPreviousData={isPreviousMetrics}
              />
            </div>
          </div>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>Total Walk Ins</span>
              <span>
                {!isLoadingMetrics &&
                  new Intl.NumberFormat('en-US').format(
                    metrics
                      ?.map((metric: { rsv: number }) => metric.rsv)
                      .reduce((acc: number, cur: number) => acc + cur, 0),
                  )}
              </span>
            </div>
            <div className={'h-[270px] p-2'}>
              <LineChart
                data={
                  metrics
                    ?.sort((a, b) => (a.date > b.date ? 1 : -1))
                    ?.map((data) => {
                      return {
                        date: dayjs(data.date).format("MMM 'YY"),
                        rsv: data.rsv,
                      }
                    }) ?? []
                }
                gradient={'blue-to-cyan'}
                yKey={'rsv'}
                xKey="date"
                isLoading={isLoadingMetrics}
                isPreviousData={isPreviousMetrics}
              />
            </div>
          </div>
        </div>
        {hasVideoImpressions && (
          <div className="flex flex-col gap-4 ">
            <span className="font-bold text-md font-Spartan">Video Metrics</span>
            <div className="grid grid-cols-2 gap-5">
              <div className={'min-h-[270px] border-2 rounded-xl'}>
                <div
                  className={
                    'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                  }>
                  <span>Total VTR</span>
                  <span>
                    {!isLoadingMetrics &&
                      formatPercentage(
                        metrics
                          ?.map(
                            (metric: { video_completions: number }) =>
                              metric.video_completions,
                          )
                          .reduce((acc: number, cur: number) => acc + cur, 0) /
                          metrics
                            ?.map(
                              (metric: { video_starts: number }) => metric.video_starts,
                            )
                            .reduce((acc: number, cur: number) => acc + cur, 0),
                      )}
                  </span>
                </div>
                <div className={'h-[270px] p-2'}>
                  <LineChart
                    data={
                      metrics
                        ?.sort((a, b) => (a.date > b.date ? 1 : -1))
                        ?.map((data) => {
                          return {
                            date: dayjs(data.date).format("MMM 'YY"),
                            vtr: (100 * data.video_completions) / data.video_starts,
                          }
                        }) ?? []
                    }
                    gradient={'blue-to-purple'}
                    yKey={'vtr'}
                    xKey="date"
                    isLoading={isLoadingMetrics}
                    isPreviousData={isPreviousMetrics}
                  />
                </div>
              </div>
              <div className={'min-h-[270px] border-2 rounded-xl'}>
                <div
                  className={
                    'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                  }>
                  <span>Quartile Completions</span>
                </div>
                <div className={'h-[270px] p-2'}>
                  <BarChart
                    data={[
                      {
                        label: '25% Completion',
                        value:
                          Math.round(
                            (10000 * barChartData?.video_1q) / barChartData?.video_starts,
                          ) / 100,
                      },
                      {
                        label: '50% Completion',
                        value:
                          Math.round(
                            (10000 * barChartData?.video_2q) / barChartData?.video_starts,
                          ) / 100,
                      },
                      {
                        label: '75% Completion',
                        value:
                          Math.round(
                            (10000 * barChartData?.video_3q) / barChartData?.video_starts,
                          ) / 100,
                      },
                      {
                        label: '100% Completion',
                        value:
                          Math.round(
                            (10000 * barChartData?.video_4q) / barChartData?.video_starts,
                          ) / 100,
                      },
                    ]}
                    isLoading={isLoadingMetrics}
                    isPreviousData={isPreviousMetrics}
                    customOptions={{
                      datalabels: {
                        formatter: (value: number): string => `${value}%`,
                      },
                      legend: {
                        display: false,
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {hasAudioImpressions && (
          <div className="flex flex-col gap-4">
            <span className="font-bold text-md font-Spartan">Audio Metrics</span>
            <div className="grid grid-cols-2 gap-5">
              <div className={'min-h-[270px] border-2 rounded-xl'}>
                <div
                  className={
                    'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                  }>
                  <span>Total LTR</span>
                  <span>
                    {!isLoadingMetrics &&
                      formatPercentage(
                        metrics
                          ?.map(
                            (metric: { audio_completions: number }) =>
                              metric.audio_completions,
                          )
                          .reduce((acc: number, cur: number) => acc + cur, 0) /
                          metrics
                            ?.map(
                              (metric: { audio_impressions: number }) =>
                                metric.audio_impressions,
                            )
                            .reduce((acc: number, cur: number) => acc + cur, 0),
                      )}
                  </span>
                </div>
                <div className={'h-[270px] p-2'}>
                  <LineChart
                    data={
                      metrics
                        ?.sort((a, b) => (a.date > b.date ? 1 : -1))
                        ?.map((data) => {
                          return {
                            date: dayjs(data.date).format("MMM 'YY"),
                            ltr: (100 * data.audio_completions) / data.audio_impressions,
                          }
                        }) ?? []
                    }
                    gradient={'blue-to-purple'}
                    yKey={'ltr'}
                    xKey="date"
                    isLoading={isLoadingMetrics}
                    isPreviousData={isPreviousMetrics}
                  />
                </div>
              </div>
              <div className={'min-h-[270px] border-2 rounded-xl'}>
                <div
                  className={
                    'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                  }>
                  <span>Quartile Completions</span>
                </div>
                <div className={'h-[270px] p-2'}>
                  <BarChart
                    data={[
                      {
                        label: '25% Completion',
                        value:
                          Math.round(
                            (10000 * barChartData?.audio_1q) /
                              barChartData?.audio_impressions,
                          ) / 100,
                      },
                      {
                        label: '50% Completion',
                        value:
                          Math.round(
                            (10000 * barChartData?.audio_2q) /
                              barChartData?.audio_impressions,
                          ) / 100,
                      },
                      {
                        label: '75% Completion',
                        value:
                          Math.round(
                            (10000 * barChartData?.audio_3q) /
                              barChartData?.audio_impressions,
                          ) / 100,
                      },
                      {
                        label: '100% Completion',
                        value:
                          Math.round(
                            (10000 * barChartData?.audio_4q) /
                              barChartData?.audio_impressions,
                          ) / 100,
                      },
                    ]}
                    isLoading={isLoadingMetrics}
                    isPreviousData={isPreviousMetrics}
                    customOptions={{
                      datalabels: {
                        formatter: (value: number): string => `${value}%`,
                      },
                      legend: {
                        display: false,
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Impressions vs Clicks</span>
          </div>
          <div className={'h-[270px] p-2'}>
            <MultiLineChart
              data={
                metrics
                  ?.sort((a, b) => (a.date > b.date ? 1 : -1))

                  ?.flatMap((data) => {
                    return [
                      {
                        date: dayjs(data.date).format("MMM 'YY"),
                        type: 'Impressions',
                        value: data.impressions,
                        axis: 'y',
                      },
                      {
                        date: dayjs(data.date).format("MMM 'YY"),
                        type: 'Clicks',
                        value: data.clicks,
                        axis: 'y2',
                      },
                    ]
                  }) ?? []
              }
              gradient={['blue-to-purple', 'red-to-orange']}
              groupKey={'type'}
              labelKey={'type'}
              yKey={'value'}
              xKey={'date'}
              isLoading={isLoadingMetrics}
              isPreviousData={isPreviousMetrics}
              customOptions={{
                y2: {
                  position: 'right', // Secondary Y-axis on the right
                  grid: {
                    drawOnChartArea: false, // Disable grid lines for y2 (optional)
                  },
                  ticks: {
                    color: '#000', // Customize tick color for y2
                  },
                },
              }}
            />
          </div>
        </div>
        <div className="flex flex-row justify-between items-center">
          <span className="font-bold text-md">Campaigns</span>
          <Button
            disabled={downloadingTable}
            variant="primary"
            onClick={async (e) => {
              e.preventDefault()
              const toastId = toast.loading('Downloading...')
              setDownloadingTable(true)
              try {
                const headers = campaignsColumns.map((column) => ({
                  // @ts-ignore
                  accessorKey: column.accessorKey,
                  header: column.header,
                }))
                await downloadCSV({
                  url: `/agencies/${agency_id}/campaigns`,
                  fetchOptions: {
                    start_date: dayjs()
                      .set('year', Number(dateFilter.value))
                      .startOf('year')
                      .format('YYYY-MM-DD'),
                    end_date: dayjs()
                      .set('year', Number(dateFilter.value))
                      .endOf('year')
                      .format('YYYY-MM-DD'),
                    dealership_id: filters?.dealership_id,
                    agency_id: agency_id,
                  },
                  sortOrder: [
                    {
                      id: 'date',
                      desc: false,
                    },
                  ],
                  headers: headers,
                  fileName: `${agency?.name}_campaigns_${dateFilter.value}.csv`,
                })
                toast.success('Downloaded table!', { id: toastId, duration: 5000 })
                setDownloadingTable(false)
              } catch (err) {
                toast.error('Failed to download table at this time.', {
                  id: toastId,
                  duration: 5000,
                })
                setDownloadingTable(false)
              }
            }}>
            Export CSV
          </Button>
        </div>
        <div className="w-full">
          <Table
            emptyLabel={'No associated campaigns'}
            columns={campaignsColumns}
            filters={{
              start_date: dayjs()
                .set('year', Number(dateFilter.value))
                .startOf('year')
                .format('YYYY-MM-DD'),
              end_date: dayjs()
                .set('year', Number(dateFilter.value))
                .endOf('year')
                .format('YYYY-MM-DD'),
              dealership_id: filters?.dealership_id,
              agency_id: agency_id,
            }}
            fetchFn={useGetCampaignsByAgencyId}
            defaultSorting={[
              {
                id: 'date',
                desc: false,
              },
            ]}
          />
        </div>
      </div>
    </div>
  )
}

export default YearlyOverview
